import { useForm, usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Pagination } from "@/components/ui/pagination"
import { LocationForm, emptyLocationFormData, type LocationFormData } from "@/components/LocationForm"
import { LocationTable } from "@/components/LocationTable"
import { FlashMessages } from "@/components/FlashMessages"
import { SearchInput } from "@/components/SearchInput"
import { Plus, MapPin } from "lucide-react"
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback"
import type { Location, LocationsPageProps } from "@/types/location"

export default function Locations() {
  const { t } = useTranslation()
  const { auth, flash, locations, pagination, filters } = usePage<LocationsPageProps>().props
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState<Location | null>(null)
  const [searchValue, setSearchValue] = useState(filters.search)

  const createForm = useForm<LocationFormData>({ ...emptyLocationFormData })
  const editForm = useForm<LocationFormData>({ ...emptyLocationFormData })

  const debouncedSearch = useDebouncedCallback((value: string) => {
    router.get("/locations", {
      search: value || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }, 300)

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value)
    debouncedSearch(e.target.value)
  }

  function handleSort(column: string) {
    const newDirection = filters.sort === column && filters.direction === "asc" ? "desc" : "asc"
    router.get("/locations", {
      search: filters.search || undefined,
      sort: column,
      direction: newDirection,
      page: 1,
    }, { preserveState: true, preserveScroll: true })
  }

  function goToPage(page: number) {
    router.get("/locations", {
      search: filters.search || undefined,
      sort: filters.sort,
      direction: filters.direction,
      page,
    }, { preserveState: true, preserveScroll: true })
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post("/locations", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingLocation) return
    editForm.patch(`/locations/${editingLocation.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingLocation(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(location: Location) {
    setEditingLocation(location)
    editForm.setData({
      name: location.name,
      street: location.street || "",
      city: location.city || "",
      state: location.state || "",
    })
  }

  function handleDelete(location: Location) {
    if (confirm(t("locations.confirmDelete"))) {
      router.delete(`/locations/${location.id}`)
    }
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("locations.title")}</h2>
            <p className="text-muted-foreground">{t("locations.subtitle")}</p>
          </div>

          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
            setIsCreateDialogOpen(open)
            if (!open) createForm.reset()
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t("locations.addButton")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("locations.addButton")}</DialogTitle>
                  <DialogDescription>{t("locations.addDescription")}</DialogDescription>
                </DialogHeader>
                <LocationForm
                  data={createForm.data}
                  errors={createForm.errors}
                  onChange={(field, value) => createForm.setData(field, value)}
                  idPrefix="create"
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={createForm.processing}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={!!editingLocation} onOpenChange={(open) => !open && setEditingLocation(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("locations.editLocation")}</DialogTitle>
                  <DialogDescription>{t("locations.editDescription")}</DialogDescription>
                </DialogHeader>
                <LocationForm
                  data={editForm.data}
                  errors={editForm.errors}
                  onChange={(field, value) => editForm.setData(field, value)}
                  idPrefix="edit"
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingLocation(null)}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t("locations.section")}
            </CardTitle>
            <CardDescription>{t("locations.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(locations.length > 0 || filters.search) && (
              <SearchInput
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={t("locations.searchPlaceholder")}
              />
            )}

            {locations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {filters.search ? t("locations.noSearchResults") : t("locations.noLocations")}
              </p>
            ) : (
              <>
                <LocationTable
                  data={locations}
                  filters={filters}
                  onSort={handleSort}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                />
                <Pagination pagination={pagination} onPageChange={goToPage} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
